import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { MoonStar, GitMerge, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const FILTERS = ["Tutte", "Maggiore", "Minore", "Altro"];

export default function Carte() {
  const [cards, setCards] = useState(null);
  const [filter, setFilter] = useState("Tutte");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/cards").then((r) => setCards(r.data)).catch(() => setCards([]));
  }, []);

  const filtered = useMemo(() => {
    if (!cards) return [];
    if (filter === "Tutte") return cards;
    return cards.filter((c) => (c.arcana || "Altro") === filter);
  }, [cards, filter]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16 sm:py-24" data-testid="carte-page">
      <div className="text-center mb-12">
        <p className="eyebrow">Le Carte</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">Il linguaggio dei simboli</h1>
        <p className="text-purple-200/70 mt-4 max-w-2xl mx-auto text-sm sm:text-base">
          Ogni carta porta con sé significati, sfumature e combinazioni: esplora il mazzo.
        </p>
      </div>

      {cards && cards.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="cards-filter-bar">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`cards-filter-${f.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-xs font-cinzel tracking-[0.15em] uppercase transition-all duration-300 border ${
                filter === f
                  ? "border-amber-500/60 text-amber-300 bg-amber-500/10"
                  : "border-[#4a2175] text-purple-300/60 hover:text-amber-200"
              }`}
            >
              {f === "Maggiore" ? "Arcani Maggiori" : f === "Minore" ? "Arcani Minori" : f}
            </button>
          ))}
        </div>
      )}

      {cards === null ? null : cards.length === 0 ? (
        <div className="mystic-card p-12 text-center max-w-xl mx-auto" data-testid="cards-empty-state">
          <MoonStar className="w-9 h-9 text-amber-400/50 mx-auto mb-4" />
          <p className="text-purple-200/70">Le carte e i loro significati verranno pubblicati qui prossimamente.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c)}
              data-testid={`card-item-${c.id}`}
              className="mystic-card overflow-hidden text-left group"
            >
              {c.image_url ? (
                <img src={c.image_url} alt={c.name} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-52 flex items-center justify-center bg-[#180a26]">
                  <MoonStar className="w-10 h-10 text-amber-400/30" />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-display text-lg text-amber-100 leading-snug">{c.name}</h3>
                {c.deck && <p className="text-xs text-purple-300/60 mt-1">{c.deck}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[#180a26] border border-amber-500/25 text-purple-50 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="card-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-amber-100">{selected.name}</DialogTitle>
              </DialogHeader>
              {selected.image_url && (
                <img src={selected.image_url} alt={selected.name} className="w-full max-h-64 object-contain rounded-lg" />
              )}
              <div className="space-y-5 text-sm">
                {selected.meaning && (
                  <div>
                    <p className="eyebrow mb-2">Significato</p>
                    <p className="text-purple-100/80 leading-relaxed whitespace-pre-line">{selected.meaning}</p>
                  </div>
                )}
                {selected.position_meanings?.length > 0 && (
                  <div>
                    <p className="eyebrow mb-2">Significato nelle posizioni</p>
                    <div className="space-y-2">
                      {selected.position_meanings.map((pm, i) => (
                        <div key={i} className="rounded-lg bg-[#0f0617] border border-[#4a2175]/60 p-3">
                          <p className="text-amber-200 font-medium">{pm.position}</p>
                          <p className="text-purple-200/70 mt-1">{pm.meaning}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selected.alternative_meanings && (
                  <div>
                    <p className="eyebrow mb-2">Significati alternativi</p>
                    <p className="text-purple-100/80 leading-relaxed whitespace-pre-line">{selected.alternative_meanings}</p>
                  </div>
                )}
                {selected.combinations?.length > 0 && (
                  <div>
                    <p className="eyebrow mb-2 flex items-center gap-2"><GitMerge className="w-3.5 h-3.5" /> Combinazioni</p>
                    <div className="space-y-2">
                      {selected.combinations.map((cb, i) => (
                        <div key={i} className="rounded-lg bg-[#0f0617] border border-fuchsia-500/25 p-3">
                          <p className="text-fuchsia-300 font-medium flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" />{cb.card}</p>
                          <p className="text-purple-200/70 mt-1">{cb.meaning}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
